import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiActivateAccount } from "../net/requests";
import styles from "./verifyPage.module.scss";
import advanceYouLogo from "../assets/images/advanceYouLogo.png";
import TextInput from "../components/form/TextInput";
import DateInput from "../components/form/DateInput";
import FormButton from "../components/form/FormButton";
import {
  getLanguageString,
  getCurrentLanguage,
  setLanguage,
} from "../misc/lang";
import { COLOR_PALETTE } from "../appConfig";
import { Visibility, VisibilityOff } from "@mui/icons-material";

const initialState = {
  newPassword: "",
  confirmPassword: "",
  nickname: "",
  firstName: "",
  lastName: "",
  dateOfBirth: "",
  gender: "",
};

export default function VerifyPageHrApp() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [form, setForm] = useState(initialState);
  const [lang, setLangState] = useState(getCurrentLanguage());
  const handleLanguageToggle = () => {
    const newLang = lang === "en" ? "sv" : "en";
    setLanguage(newLang);
    setLangState(newLang);
    window.location.reload();
  };
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const token = searchParams.get("token") || "";
  const email = searchParams.get("email") || "";
  const hasActivationData = Boolean(token && email);

  const allRequiredFilled =
    email && form.newPassword && form.confirmPassword && form.firstName;

  const handleChange = (name: string, value: string) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleActivate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    setSuccess(false);
    if (
      !email ||
      !form.newPassword ||
      !form.confirmPassword ||
      !form.firstName
    ) {
      setError(getLanguageString("verify", "all_fields_required"));
      return;
    }
    if (form.newPassword !== form.confirmPassword) {
      setError(getLanguageString("verify", "passwords_do_not_match"));
      return;
    }
    setLoading(true);
    try {
      const payload = {
        token,
        email,
        new_password: form.newPassword,
        nickname: form.nickname,
        first_name: form.firstName,
        last_name: form.lastName,
        date_of_birth: form.dateOfBirth,
        gender: form.gender,
      };
      await apiActivateAccount(payload);
      setSuccess(true);
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
          e?.message ||
          getLanguageString("verify", "error"),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles["verify-page-container"]}>
      <div className={styles["verify-card"]}>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: 8,
          }}
        >
          <button
            onClick={handleLanguageToggle}
            style={{
              background: "#f7f7fb",
              border: "1px solid #e0e0e0",
              borderRadius: 16,
              padding: "4px 16px",
              fontWeight: 600,
              fontSize: 14,
              cursor: "pointer",
              color: "#222",
              outline: "none",
              minWidth: 80,
              transition: "background 0.2s",
            }}
            aria-label="Switch language"
          >
            {getCurrentLanguage() === "en" ? "🇸🇪 Svenska" : "🇬🇧 English"}
          </button>
        </div>
        <img
          src={advanceYouLogo}
          alt="AdvanceYou Logo"
          style={{
            display: "block",
            margin: "0 auto 18px",
            maxWidth: 120,
            borderRadius: "50%",
          }}
        />
        <h2 style={{ color: "#000", textAlign: "center" }}>
          {getLanguageString("verify", "title")}
        </h2>
        {success ? (
          <div
            className={styles["success"]}
            style={{ margin: 24, textAlign: "center" }}
          >
            {getLanguageString("verify", "activated")}
          </div>
        ) : !hasActivationData ? (
          <>
            <p>{getLanguageString("verify", "missing_link")}</p>
            <button
              className={styles["secondary-btn"]}
              onClick={() => navigate("/login")}
              style={{ borderColor: COLOR_PALETTE[0], color: COLOR_PALETTE[0] }}
            >
              {getLanguageString("verify", "go_to_login")}
            </button>
          </>
        ) : (
          <form onSubmit={handleActivate}>
            <TextInput
              label={getLanguageString("verify", "email")}
              initialValue={email}
              updateValue={() => {}}
              readOnly
            />
            <TextInput
              label={getLanguageString("verify", "first_name")}
              initialValue={form.firstName}
              updateValue={(v) => handleChange("firstName", v)}
              isRequired
            />
            <TextInput
              label={getLanguageString("verify", "last_name")}
              initialValue={form.lastName}
              updateValue={(v) => handleChange("lastName", v)}
            />
            <TextInput
              label={getLanguageString("verify", "nickname")}
              initialValue={form.nickname}
              updateValue={(v) => handleChange("nickname", v)}
            />
            <TextInput
              label={getLanguageString("verify", "new_password")}
              initialValue={form.newPassword}
              updateValue={(v) => handleChange("newPassword", v)}
              type={showNewPassword ? "text" : "password"}
              endIcon={showNewPassword ? <VisibilityOff /> : <Visibility />}
              iconAction={() => setShowNewPassword((v) => !v)}
              isRequired
            />
            <TextInput
              label={getLanguageString("verify", "confirm_password")}
              initialValue={form.confirmPassword}
              updateValue={(v) => handleChange("confirmPassword", v)}
              type={showConfirmPassword ? "text" : "password"}
              endIcon={showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              iconAction={() => setShowConfirmPassword((v) => !v)}
              isRequired
            />
            <DateInput
              label={getLanguageString("verify", "date_of_birth")}
              initialValue={form.dateOfBirth}
              updateValue={(v) => handleChange("dateOfBirth", v)}
            />
            <div style={{ marginTop: 12 }}>
              <label style={{ fontWeight: 500 }}>
                {getLanguageString("verify", "gender")}
              </label>
              <select
                name="gender"
                value={form.gender}
                onChange={(e) => handleChange("gender", e.target.value)}
                style={{
                  width: "100%",
                  padding: 10,
                  borderRadius: 8,
                  border: `1px solid ${COLOR_PALETTE[0]}`,
                }}
              >
                <option value="">
                  {getLanguageString("verify", "gender")}
                </option>
                <option value="male">
                  {getLanguageString("verify", "gender_male")}
                </option>
                <option value="female">
                  {getLanguageString("verify", "gender_female")}
                </option>
                <option value="not_share">
                  {getLanguageString("verify", "gender_not_share")}
                </option>
              </select>
            </div>
            {error && <div className={styles["error"]}>{error}</div>}
            <div style={{ marginTop: 20 }}>
              <button
                type="submit"
                disabled={!allRequiredFilled || loading}
                style={{
                  width: "100%",
                  background: !(!allRequiredFilled || loading)
                    ? "#132829"
                    : "#bdbdbd",
                  color: "#fff",
                  fontWeight: 700,
                  border: "none",
                  borderRadius: 8,
                  padding: "12px 0",
                  fontSize: "1.1rem",
                  cursor: !(!allRequiredFilled || loading)
                    ? "pointer"
                    : "not-allowed",
                  opacity: !(!allRequiredFilled || loading) ? 1 : 0.6,
                  transition: "background 0.2s, opacity 0.2s",
                }}
              >
                {loading
                  ? getLanguageString("verify", "activating")
                  : getLanguageString("verify", "activate")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
